import type { z } from 'zod';

import type { LessonSchema } from './schemas';

type LessonField = keyof z.infer<typeof LessonSchema>;

// A lesson's id and every part it holds
const identityAttributes = [
  'id',
  'subject',
  'classCode',
  'courseId',
  'lessonTemplateCode',
  'counter',
] as const satisfies readonly LessonField[];

const staffGetAttributes = [
  ...identityAttributes,
  'classId',
  'lessonTemplateId',
  'classLabel',
  'startDatetime',
  'studentCount',
  'absenteeCount',
  'rollMarked',
  'notes',
  'location',
  'roomId',
  'duration',
  'makeUpCount',
  'onlineCount',
  'teacherId',
  'enrolledStudents',
  'billingType',
  'homeworkMarked',
  'separatelyBilled',
  'streamId',
  'questionSetId',
] as const satisfies readonly LessonField[];

const staffListAttributes = [...staffGetAttributes] as const satisfies readonly LessonField[];

const studentListAttributes = [
  ...identityAttributes,
  'classId',
  'lessonTemplateId',
  'classLabel',
  'startDatetime',
  'studentCount',
  'rollMarked',
  'location',
  'roomId',
  'duration',
  'teacherId',
  // Needed so the student-facing reschedule UI can keep a makeup within the same billing
  // identity as the lesson being replaced (a separately-billed lesson must not be made up
  // into a non-separately-billed one, or vice versa).
  'separatelyBilled',
  'streamId',
] as const satisfies readonly LessonField[];

const timesheetAttributes = [
  ...identityAttributes,
  'classId',
  'lessonTemplateId',
  'classLabel',
  'startDatetime',
  'teacherId',
] as const satisfies readonly LessonField[];

const teacherUpdateAttributes = [
  'notes',
  'homeworkMarked',
] as const satisfies readonly LessonField[];

const adminUpdateAttributes = [
  'startDatetime',
  'notes',
  'location',
  'roomId',
  'billingType',
  'duration',
  'teacherId',
  'homeworkMarked',
] as const satisfies readonly LessonField[];

export const LessonAttributes = {
  StaffGet: staffGetAttributes,
  StaffList: staffListAttributes,
  StudentList: studentListAttributes,
  Timesheet: timesheetAttributes,
  TeacherUpdate: teacherUpdateAttributes,
  AdminUpdate: adminUpdateAttributes,
};
